import React, { FC, useState, useEffect, useCallback } from 'react';
import { noop } from 'lodash';
import classNames from 'classnames';
import { PopoverOrigin } from '@material-ui/core';
import useStyles from './Popover.styles';
import { getPositioningStyle, getScrollbarSize } from './helpers';

export interface Position {
  top: string;
  left: string;
  transformOrigin: string;
}

export interface PopoverProps {
  dataQaId: string;
  children: React.ReactNode;
  anchorEl: HTMLElement | null;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  onPopoverClose?: () => void;
  isOpen: boolean;
  passedClassName?: string | null;
}

const Popover: FC<PopoverProps> = ({
  children,
  dataQaId,
  anchorEl,
  onPopoverClose = noop,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'right',
  },
  isOpen = false,
  passedClassName = null,
}: PopoverProps) => {
  const classes = useStyles();
  const [element, setElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (document.body.scrollHeight > window.innerHeight && getScrollbarSize()) {
        document.body.style.paddingRight = `${getScrollbarSize()}px`;
      }
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }, [isOpen]);

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode === 27) {
        e.preventDefault();
        onPopoverClose();
      }
    },
    [onPopoverClose],
  );

  const handleOutsideClick = useCallback(
    (e: MouseEvent) => {
      if (element && !element.contains(e.target as Node)) {
        onPopoverClose();
      }
    },
    [onPopoverClose, element],
  );

  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp, false);
    document.addEventListener('click', handleOutsideClick, false);

    return () => {
      window.removeEventListener('keyup', handleKeyUp, false);
      document.removeEventListener('click', handleOutsideClick, false);
    };
  }, [handleKeyUp, handleOutsideClick]);

  const position = getPositioningStyle(element, anchorEl, anchorOrigin, transformOrigin);

  const popover = isOpen && (
    <div
      ref={setElement}
      data-qa-id={`${dataQaId}-popover`}
      className={classNames(classes.root, passedClassName)}
      style={{ top: position?.top, left: position?.left, transformOrigin: position?.transformOrigin, zIndex: 1350 }}
    >
      {children}
    </div>
  );

  return popover || null;
};

export default Popover;
